import React from "react";
import { FaExternalLinkAlt } from "react-icons/fa";

/**
 * Normalized status / error strip shown UNDER the pay button
 * (not above, as the previous design did) so the primary action
 * never shifts vertically between phases.
 *
 * Accepts a normalized `phase` enum assembled by CheckoutPanel
 * from either useX402Pay or useDirectPay state, plus optional
 * direct-method confirmation counters + explorer URL.
 *
 * phase ∈
 *   'idle' | 'switching' | 'signing' | 'submitting' |
 *   'sending' | 'attesting' | 'confirming' | 'recording' |
 *   'password' | 'error'
 *
 * When phase === 'idle' the strip renders nothing.
 */
export function PayStatusStrip({
  phase,
  error,
  network,
  confirmations,
  requiredConfirmations,
  explorerUrl,
}) {
  if (phase === "idle") return null;

  if (phase === "error") {
    // Backend 402 responses carry a `reason` alongside the headline
    // `error` (e.g. "nonce already used", "amount mismatch"). Surface
    // it so the payer / support can diagnose without digging through
    // devtools. Both hooks attach `err.reason` in the same shape.
    return (
      <div className="pay-status pay-status--error">
        {error?.message || "Payment failed. Please try again."}
        {error?.reason ? (
          <div className="pay-status__reason">{error.reason}</div>
        ) : null}
      </div>
    );
  }

  if (phase === "switching") {
    return (
      <div className="pay-status pay-status--info">
        Switching your wallet to {network}…
      </div>
    );
  }

  if (phase === "signing") {
    return (
      <div className="pay-status pay-status--info">
        Sign the authorization in your wallet to continue.
      </div>
    );
  }

  if (phase === "submitting") {
    return (
      <div className="pay-status pay-status--info">
        Settling on-chain — this usually takes a few seconds.
      </div>
    );
  }

  if (phase === "password") {
    return (
      <div className="pay-status pay-status--info">
        Enter your wallet password to authorize the payment.
      </div>
    );
  }

  if (phase === "sending") {
    return (
      <div className="pay-status pay-status--info">
        <strong>Step 1 of 2:</strong> Send the payment. Confirm the transfer in
        your wallet.
      </div>
    );
  }

  if (phase === "attesting") {
    return (
      <div className="pay-status pay-status--info">
        <strong>Step 2 of 2:</strong> Sign a confirmation in your wallet to
        prove this transfer is yours. No gas, no funds move.
      </div>
    );
  }

  if (phase === "confirming") {
    return (
      <div className="pay-status pay-status--info">
        Waiting for on-chain confirmations
        {requiredConfirmations
          ? ` (${confirmations}/${requiredConfirmations})`
          : ""}
        {explorerUrl && (
          <>
            {" "}
            <a
              href={explorerUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="pay-direct-info__tx-link"
            >
              View on explorer <FaExternalLinkAlt size={10} />
            </a>
          </>
        )}
      </div>
    );
  }

  if (phase === "recording") {
    return (
      <div className="pay-status pay-status--info">Recording payment…</div>
    );
  }

  return null;
}
